import { FlatTreeControl } from '@angular/cdk/tree';
import { MatTreeFlatDataSource, MatTreeFlattener } from '@angular/material/tree';
import { LiveState } from '@live/store/models/live.models';
import { getIsNodeExpandable, getNodeChildren, getNodeLevel, getRundownsSearchResultsTreeLikeData, getRundownsTreeLikeData, nodeTransformer } from '@live/utils/live-search-result-utils';
import { createFeatureSelector, createSelector } from '@ngrx/store';
import { PSTFieldNames } from '@src/suite/common/data-models/fields/pst-field-names';
import { SingleViewStateRecord } from '@src/suite/common/data-models/records/single-viewstate-record';
import { ResultGroup } from '@src/suite/common/data-models/search-results/result-group';
import { TreeLikeFlatNode, TreeLikeNode } from '@src/suite/common/data-models/search-results/search-results';
import { ViewStateTypesEnum } from '@src/suite/common/enums/viewstate-types';
import { selectAutoCreateMosDevices, selectGroupSortDefs } from '@src/suite/common/store/global-tables/global-tables.selectors';
import { selectListGuids, selectNodeExpandState } from '@src/suite/common/store/list/list.selectors';
import { selectCurrentViewState } from '@src/suite/common/store/navigation/selectors/viewstate.selectors';
import { selectRundownRecordsMap } from '@src/suite/common/store/records/records.selectors';

const selectLiveState = createFeatureSelector<LiveState>('live');

export const selectGroupedSortedRundowns = createSelector(
    selectCurrentViewState,
    selectGroupSortDefs,
    selectListGuids,
    selectRundownRecordsMap,
    (currentViewState, groupSortDefs, listGuids, rundownRecords) => {
        const groupBy = (currentViewState as SingleViewStateRecord)?.viewStateGrouping?.Value || PSTFieldNames.TargetPublicationDate;
        const groupByOrder = (currentViewState as SingleViewStateRecord)?.viewStateGroupSortOrder || 'asc';
        const sortBy = (currentViewState as SingleViewStateRecord)?.viewStateSorting?.Value || PSTFieldNames.TargetPublicationDate;
        const sortByOrder = (currentViewState as SingleViewStateRecord)?.viewStateSortOrder || 'asc';
        const rundownGroup = new ResultGroup<StorytellingRundownRecord>();
        const rundownGroupingDef = groupSortDefs?.[groupBy];
        const rundownSortingDef = groupSortDefs?.[sortBy];
        const rundowns = listGuids.map(guid => rundownRecords.get(guid) || null).filter(val => !!val);
        if (!rundownGroupingDef || !rundownSortingDef) {
            return rundownGroup;
        }

        if (currentViewState?.viewStateType !== ViewStateTypesEnum.ROLIST && currentViewState?.viewStateType !== ViewStateTypesEnum.SEARCH) {
            return null;
        }

        for (const rundown of rundowns) {
            rundownGroup.addToGroups(rundownGroupingDef.valueConvertor(rundown), rundown);
        }
        rundownGroup.sort(rundownGroupingDef.groupKeyComparator(groupByOrder), rundownSortingDef.recordComparator(sortByOrder));
        return rundownGroup;
    }
);

export const selectTreeDataSourceControlPair = createSelector(
    selectCurrentViewState,
    selectGroupedSortedRundowns,
    selectNodeExpandState,
    (currentVs, records: ResultGroup<StorytellingRundownRecord>, nodesExpandState) => {
        if (!currentVs) return {};
        const treeControl: FlatTreeControl<TreeLikeFlatNode<StorytellingRecord>, string> = new FlatTreeControl<TreeLikeFlatNode<StorytellingRecord>, string>(getNodeLevel, getIsNodeExpandable, {
            trackBy: node => node.uniqueId,
        });
        const treeFlattener = new MatTreeFlattener<TreeLikeNode<StorytellingRecord>, TreeLikeFlatNode<StorytellingRecord>, string>(nodeTransformer, getNodeLevel, getIsNodeExpandable, getNodeChildren);
        // Populates our flattened data into the tree control
        const treeDataSource = new MatTreeFlatDataSource(treeControl, treeFlattener);

        switch (currentVs?.viewStateType) {
            case ViewStateTypesEnum.SEARCH:
                treeDataSource.data = getRundownsSearchResultsTreeLikeData(records as ResultGroup<StorytellingRundownRecord>);
                break;
            case ViewStateTypesEnum.ROLIST:
                treeDataSource.data = getRundownsTreeLikeData(records as ResultGroup<StorytellingRundownRecord>);
                break;
            default:
                [];
        }
        if (!treeControl?.dataNodes) return null;
        treeControl.expandAll();
        treeControl.dataNodes.forEach(node => {
            const state = nodesExpandState[node.id];
            if (state != null) {
                if (state === false) {
                    treeControl.collapseDescendants(node);
                } else {
                    treeControl.expandDescendants(node);
                }
            }
        });
        return { treeDataSource, treeControl };
    }
);

export const selectTreeDataSource = createSelector(selectTreeDataSourceControlPair, pair => pair?.treeDataSource);
export const selectTreeControl = createSelector(selectTreeDataSourceControlPair, pair => pair?.treeControl);

export const selectCurrentCardItemHeights = createSelector(selectTreeControl, treeControl => {
    if (!treeControl?.dataNodes) return [];
    const heights = treeControl.dataNodes.map(item => {
        switch (item.level) {
            case 0:
                return 40;
            default:
                return 117;
        }
    });
    return heights;
});

export const selectCurrentCondesedItemHeights = createSelector(selectTreeControl, treeControl => {
    if (!treeControl?.dataNodes) return [];
    const heights = treeControl.dataNodes.map(item => {
        switch (item.level) {
            case 0:
                return 32;
            default:
                return 60;
        }
    });
    return heights;
});

export const selectSavedViewStates = createSelector(selectLiveState, state => state.storedViewStates);

export const selectAutoCreateMosDeviceItems = createSelector(selectAutoCreateMosDevices, devices => {
    const items = devices.map(device => {
        return { id: device.ID, title: device.AliasValue || device.ID, definedGroups: device.ObjectGroups?.split('|') || [], objectType: device.ObjectType || '' };
    });
    return [{ id: '', title: ' ', definedGroups: [], objectType: '' }, ...items];
});
